import { faLock } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import mixpanel from "mixpanel-browser";
import { useState } from "react";

import UpgradeModal from "./UpgradeModal";
import { usePremiumFeature } from "../../../hooks";

const PremiumFeatureGate = ({
  children,
  featureName,
  iconPosition = "center",
  iconSize = "sm",
  requiredPermission,
  className = "",
}) => {
  const { hasFeatureAccess } = usePremiumFeature();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // If user has required permission and we're not forcing the gate, render children as-is
  if (hasFeatureAccess(requiredPermission)) {
    return children;
  }

  const handleOpenModal = (e) => {
    e?.stopPropagation();
    e?.preventDefault();
    setShowUpgradeModal(true);
    mixpanel.track("Clicked Premium Feature Gate", {
      featureName,
      requiredPermission,
    });
  };

  // Get the icon size dimensions
  const getIconSizeStyles = () => {
    switch (iconSize) {
      case "xs":
        return {
          width: "18px",
          height: "18px",
          padding: "3px",
          fontSize: "0.7rem",
        };
      case "sm":
        return {
          width: "24px",
          height: "24px",
          padding: "4px",
          fontSize: "0.85rem",
        };
      case "lg":
        return {
          width: "40px",
          height: "40px",
          padding: "7px",
          fontSize: "1.35rem",
        };
      default:
        return {
          width: "32px",
          height: "32px",
          padding: "5px",
          fontSize: "1.1rem",
        };
    }
  };

  // Get the icon position styles
  const getIconPositionStyles = () => {
    switch (iconPosition) {
      case "middle-right":
        return {
          top: "50%",
          right: "8px",
          transform: "translateY(-50%)",
        };
      case "center":
        return {
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        };
      case "top-right":
      default:
        return {
          top: "-8px",
          right: "-8px",
        };
    }
  };

  const sizeStyles = getIconSizeStyles();
  const positionStyles = getIconPositionStyles();

  return (
    <>
      <UpgradeModal
        shouldShow={showUpgradeModal}
        setShouldShow={setShowUpgradeModal}
        featureName={featureName}
      />
      <div
        className={`relative inline-block w-fit h-fit cursor-pointer ${className}`}
        onClick={handleOpenModal}
        data-testid={`premium-feature-gate-${featureName}`}
      >
        <div className="opacity-50 relative z-1 h-full w-full pointer-events-none grayscale-50">
          {children}
        </div>
        {/* Clickable overlay to capture clicks */}
        <div className="absolute inset-0 z-2 cursor-pointer" />
        <div className="bg-transparent absolute inset-0 pointer-events-none z-3">
          <div
            className="lock-icon rounded-full p-1.5 flex items-center justify-center absolute z-20"
            style={{
              ...sizeStyles,
              ...positionStyles,
              backgroundColor: "var(--surface-brand)",
            }}
          >
            <FontAwesomeIcon
              icon={faLock}
              size={iconSize}
              className="text-yellow-300"
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default PremiumFeatureGate;
